import Lenis from 'lenis';

const initAdjustZoom = () => {
  const BREAKPOINTS = [
    { maxWidth: 360, width: 360 },
    { maxWidth: 480, width: 480 },
    { maxWidth: 960, width: 960 },
    { maxWidth: 1440, width: 1440 },
    { maxWidth: 1920, width: 1920 },
  ];

  const DEFAULT_WIDTH = 1920;

  const wrapper = document.querySelector(".wrapper");
  if (!wrapper) return;

  let currentZoom = -1;
  let rafId = null;

  const getViewportWidth = () => {
    return window.visualViewport?.width ?? window.innerWidth;
  };

  const getContainerWidth = (viewportWidth) => {
    const bp = BREAKPOINTS.find(
      ({ maxWidth }) => viewportWidth <= maxWidth
    );

    return bp ? bp.width : DEFAULT_WIDTH;
  };

  const adjustZoom = () => {
    const viewportWidth = getViewportWidth();
    const containerWidth = getContainerWidth(viewportWidth);

    const zoom = viewportWidth / containerWidth;

    if (Math.abs(currentZoom - zoom) < 0.001) return;

    currentZoom = zoom;
    wrapper.style.zoom = zoom;
  };

  const scheduleAdjust = () => {
    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      rafId = null;
      adjustZoom();
    });
  };

  adjustZoom();

  window.addEventListener("resize", scheduleAdjust, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", scheduleAdjust, {
      passive: true,
    });
  }

  window.addEventListener("orientationchange", scheduleAdjust);
};

const initHeaderScroll = ({
  headerSelector,
  darkSectionSelector,
  lightSectionSelector,
  lightClass,
  darkClass,
  headerHiddenClass,
  burgerOpenClass,
  scrollThreshold = 100,
}) => {
  const header = document.querySelector(headerSelector);
  if (!header) return;

  const sections = Array.from(
    document.querySelectorAll(`${darkSectionSelector}, ${lightSectionSelector}`)
  );

  let lastScrollY = window.scrollY;

  const updateHeader = () => {
    const scrollY = window.scrollY;
    const isBurgerOpen = header.classList.contains(burgerOpenClass);

    if (scrollY <= scrollThreshold || isBurgerOpen) {
      header.classList.remove(headerHiddenClass);
    } else if (scrollY > lastScrollY) {
      header.classList.add(headerHiddenClass);
    } else if (scrollY < lastScrollY) {
      header.classList.remove(headerHiddenClass);
    }

    lastScrollY = scrollY;

    if (scrollY < scrollThreshold) {
      header.classList.remove(lightClass, darkClass);
      return;
    }

    const headerBottom = header.getBoundingClientRect().bottom;

    const currentSection = sections.find((section) => {
      const rect = section.getBoundingClientRect();
      return rect.top <= headerBottom && rect.bottom > headerBottom;
    });

    if (!currentSection) return;

    if (currentSection.matches(darkSectionSelector)) {
      header.classList.add(lightClass);
      header.classList.remove(darkClass);
    } else if (currentSection.matches(lightSectionSelector)) {
      header.classList.add(darkClass);
      header.classList.remove(lightClass);
    }
  };

  updateHeader();
  window.addEventListener("scroll", updateHeader, { passive: true });
};

const initHeaderDropdown = ({
  itemSelector,
  contentSelector,
  activeClass = "is-active",
}) => {
  const items = document.querySelectorAll(itemSelector);
  const contents = document.querySelectorAll(contentSelector);

  if (!items.length || !contents.length) return;

  let activeIndex = 0;

  const showContent = (index) => {
    if (activeIndex === index) return;

    contents[activeIndex]?.classList.remove(activeClass);
    contents[index]?.classList.add(activeClass);

    activeIndex = index;
  };

  contents[0].classList.add(activeClass);

  items.forEach((item, index) => {
    item.addEventListener("mouseenter", () => {
      showContent(index);
    });
  });
};

const initHeaderBurger = ({
  headerSelector,
  burgerSelector,
  menuSelector,
  activeClass = "is-open-burger",
}) => {
  const header = document.querySelector(headerSelector);
  const burger = document.querySelector(burgerSelector);
  const menu = document.querySelector(menuSelector);

  if (!header || !burger || !menu) return;

  const setMenuOpen = (isOpen) => {
    header.classList.toggle(activeClass, isOpen);
    burger.setAttribute("aria-expanded", String(isOpen));
  };

  burger.addEventListener("click", () => {
    setMenuOpen(!header.classList.contains(activeClass));
  });

  document.addEventListener("click", (event) => {
    if (
      !header.classList.contains(activeClass) ||
      menu.contains(event.target) ||
      burger.contains(event.target)
    ) {
      return;
    }

    setMenuOpen(false);
  });
};

const initHeaderAccordion = ({
  itemSelector,
  triggerSelector,
  wrapperSelector,
  activeClass = "is-active",
  closeOthers = true,
}) => {
  const items = document.querySelectorAll(itemSelector);

  if (!items.length) return;

  const closeItem = (item) => {
    const wrapper = item.querySelector(wrapperSelector);

    item.classList.remove(activeClass);

    if (wrapper) {
      wrapper.style.height = `${wrapper.scrollHeight}px`;

      requestAnimationFrame(() => {
        wrapper.style.height = "0px";
      });
    }
  };

  const openItem = (item) => {
    const wrapper = item.querySelector(wrapperSelector);

    item.classList.add(activeClass);

    if (wrapper) {
      wrapper.style.height = `${wrapper.scrollHeight}px`;

      const onTransitionEnd = () => {
        if (item.classList.contains(activeClass)) {
          wrapper.style.height = "auto";
        }

        wrapper.removeEventListener("transitionend", onTransitionEnd);
      };

      wrapper.addEventListener("transitionend", onTransitionEnd);
    }
  };

  items.forEach((item) => {
    const trigger = item.querySelector(triggerSelector);
    const wrapper = item.querySelector(wrapperSelector);

    if (!trigger || !wrapper) return;

    wrapper.style.height = "0px";
    wrapper.style.overflow = "hidden";

    trigger.addEventListener("click", () => {
      const isOpen = item.classList.contains(activeClass);

      if (closeOthers) {
        items.forEach((otherItem) => {
          if (otherItem !== item) {
            closeItem(otherItem);
          }
        });
      }

      if (isOpen) {
        closeItem(item);
      } else {
        openItem(item);
      }
    });
  });
};

const initModal = ({
  triggerSelector,
  modalSelector,
  closeSelector,
  activeClass = 'is-open',
}) => {
  const modal = document.querySelector(modalSelector);
  if (!modal) return;

  const triggers = document.querySelectorAll(triggerSelector);
  const closes = modal.querySelectorAll(closeSelector);

  const openModal = () => {
    modal.classList.add(activeClass);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    modal.classList.remove(activeClass);
    document.body.style.overflow = '';
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', openModal);
  });

  closes.forEach((button) => {
    button.addEventListener('click', closeModal);
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.classList.contains(activeClass)) {
      closeModal();
    }
  });
};

const initHeaderNavDropdown = ({
  itemSelector,
  triggerSelector,
  dropdownSelector,
  activeClass = "is-active",
}) => {
  const item = document.querySelector(itemSelector);

  if (!item) return;

  const trigger = item.querySelector(triggerSelector);
  const dropdown = item.querySelector(dropdownSelector);

  if (!trigger || !dropdown) return;

  let isOpen = false;

  const open = () => {
    isOpen = true;
    item.classList.add(activeClass);
    trigger.classList.add(activeClass);
    dropdown.classList.add(activeClass);
  };

  const close = () => {
    isOpen = false;
    item.classList.remove(activeClass);
    trigger.classList.remove(activeClass);
    dropdown.classList.remove(activeClass);
  };

  trigger.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    isOpen ? close() : open();
  });

  document.addEventListener("click", (e) => {
    if (!isOpen) return;

    if (!item.contains(e.target)) {
      close();
    }
  });

  window.addEventListener(
    "scroll",
    () => {
      if (isOpen) {
        close();
      }
    },
    { passive: true }
  );
};

document.addEventListener('DOMContentLoaded', () => {
  document.body.classList.add('is-loaded');

  const lenis = new Lenis({
    autoRaf: true,
    duration: 1.2,
    smoothWheel: true,
    wheelMultiplier: 1,
    touchMultiplier: 2,
  });

  initAdjustZoom();
  
  initHeaderScroll({
    headerSelector       : '.header',
    darkSectionSelector  : '.is-dark',
    lightSectionSelector : '.is-light',
    lightClass           : 'header--light',
    darkClass            : 'header--dark',
    headerHiddenClass: "header--hidden",
    burgerOpenClass: "is-open-burger",
    scrollThreshold      : 100,
  });

  initHeaderDropdown({
    itemSelector: ".header__nav-subitem",
    contentSelector: ".header__nav-content",
    activeClass: "is-active",
  });

  initHeaderBurger({
    headerSelector: ".header",
    burgerSelector: ".header__burger",
    menuSelector: ".header__menu",
    activeClass: "is-open-burger",
  });

  initHeaderAccordion({
    itemSelector: ".header__menu-box",
    triggerSelector: ".header__menu-head",
    wrapperSelector: ".header__menu-wrapper",
    activeClass: "is-active",
    closeOthers: true,
  });

  initModal({
    triggerSelector: '[data-modal-callback-button]',
    modalSelector: '[data-modal-callback]',
    closeSelector: '[data-modal-callback-close]',
  });

  initHeaderNavDropdown({
    itemSelector: ".header__nav-item:last-child",
    triggerSelector: ".header__nav-link",
    dropdownSelector: ".header__nav-dropdown",
  });
});